import React, { useMemo } from "react";
import {
  Control,
  Controller,
  FieldArrayWithId,
  FieldErrors,
  useWatch,
} from "react-hook-form";
import {
  LuAlertCircle,
  LuBoxes,
  LuIndianRupee,
  LuPackage,
  LuPlus,
  LuTrash2,
} from "react-icons/lu";

import MaterialPhoto from "./MaterialPhoto";
import Input from "../ui/Input";
import RateInput from "../ui/RateInput";
import cx from "../ui/cx";
import {
  arrayAddButtonClass,
  arrayRemoveButtonClass,
  arrayRowClass,
  fieldErrorIconClass,
  fieldErrorTextClass,
  fieldInputClass,
  fieldLabelClass,
  formFieldIconClass,
} from "../ui/styles";
import { hasRate } from "../../static/processRate";
import {
  emptyLine,
  formatAmount,
  formatQty,
  isWholeQty,
  lineAmount,
  lineKey,
  sanitizeQty,
} from "../../static/materialLine";
import {
  IMaterialLinesFormInput,
  LineHintProps,
  MaterialOptionProps,
} from "../../types/materialLine";
import { OrderLineProps } from "../../types/materialOrder";

type Props = {
  control: Control<IMaterialLinesFormInput>;
  fields: FieldArrayWithId<IMaterialLinesFormInput, "items", "id">[];
  errors: FieldErrors<IMaterialLinesFormInput>;
  onRemove: (index: number) => void;
  // A row's qty is about to change from previous to qty (a purchase row that receives an order line may tick or untick
  // its "ઓર્ડર બંધ").
  onQtyChange: (index: number, previous: string, qty: string) => void;
  onOpenPicker: () => void;
  materials: MaterialOptionProps[];
  hints: Record<string, LineHintProps>;
  hasParty: boolean;
  // The picker waits for the party's open order lines.
  isPickerLoading: boolean;
  // A purchase row may receive an open order's line: it shows what that line still awaits and can close it.
  isPurchase: boolean;
  orderLineById: Map<number, OrderLineProps>;
  // Only a purchase line has a price and an amount, and only for a user who may see rates.
  withPrice: boolean;
};

const rowTitleClass =
  "md:col-span-12 flex items-center justify-between gap-2 text-sm font-semibold text-[#113F67]";

const hintTextClass = "text-xs text-gray-500";

const checkboxClass =
  "h-4 w-4 flex-shrink-0 cursor-pointer rounded border-gray-300 accent-[#113F67]";

// Column widths of a row on wider screens, after its one-column photo; an order row has no price or amount.
const columnClass = {
  order: {
    material: "md:col-span-7",
    qty: "md:col-span-3",
  },
  purchase: {
    material: "md:col-span-4",
    qty: "md:col-span-2",
  },
};

// The order or purchase lines: materials come from the picker, and each row's qty and price can be changed here. A row
// starts with the material's photo, like a cutting order row. A line's material size comes from the picker and isn't
// shown: the materials module doesn't show sizes (a new material has the one 3660 size). A purchase row that receives
// an order line can close the order line.
const MaterialLinesForm = ({
  control,
  fields,
  errors,
  onRemove,
  onQtyChange,
  onOpenPicker,
  materials,
  hints,
  hasParty,
  isPickerLoading,
  isPurchase,
  orderLineById,
  withPrice,
}: Props) => {
  const items = useWatch({ control, name: "items" }) || [];
  const columns = withPrice ? columnClass.purchase : columnClass.order;

  const materialById = useMemo(
    () => new Map(materials.map((material) => [material.id, material])),
    [materials]
  );

  const pickerLabel = !hasParty
    ? "પહેલા પાર્ટી પસંદ કરો"
    : isPickerLoading
    ? "ઓર્ડર લોડ થઈ રહ્યા છે..."
    : "મટીરીયલ પસંદ કરો";

  const canPick = hasParty && !isPickerLoading;

  return (
    <div className="space-y-4">
      <label className={fieldLabelClass}>
        મટીરીયલ્સ <span className="text-red-500">*</span>
      </label>

      {fields.map((field, index) => {
        const item = items[index] || emptyLine;
        const material = materialById.get(item.material_id);
        const hint = hints[lineKey(item.material_id, item.material_size_id)];
        const locked = (hint?.received || 0) > 0;
        const isOrderLine = item.order_item_id > 0;
        const orderLine = isOrderLine
          ? orderLineById.get(item.order_item_id)
          : undefined;

        const rowErrors = errors?.items?.[index];
        const materialError = rowErrors?.material_id;
        const qtyError = rowErrors?.qty;
        const priceError = rowErrors?.price;

        const removeButton = fields.length > 1 && !locked && (
          <button
            type="button"
            title="કાઢી નાખો"
            className={arrayRemoveButtonClass}
            onClick={() => onRemove(index)}
          >
            <LuTrash2 className="h-5 w-5" />
          </button>
        );

        // An order row says what the party's other open orders still await of its material. A purchase row says what
        // its order line still awaits; one without an order line reduces no order, so it says nothing.
        const pendingText = !isPurchase
          ? hint?.pending
            ? `ઓર્ડર બાકી: ${formatQty(hint.pending)}`
            : null
          : orderLine
          ? `બાકી: ${formatQty(orderLine.pending_qty)}`
          : null;

        return (
          <div key={field.id} className={arrayRowClass}>
            <div className={rowTitleClass}>
              <span className="flex flex-wrap items-center gap-x-2">
                મટીરીયલ - {index + 1}
                {isOrderLine && (
                  <span className="rounded bg-[#113F67]/10 px-1.5 py-0.5 text-xs font-semibold">
                    ઓર્ડર #{item.order_id}
                  </span>
                )}
              </span>
              <span className="md:hidden">{removeButton}</span>
            </div>

            {/* The material's photo comes first, so the line is known at a glance. */}
            <div className="md:col-span-1 md:self-center">
              <MaterialPhoto
                name={material?.name || "-"}
                imageUrl={material?.image_url}
                className="h-20 w-20 rounded-md border border-gray-200 md:aspect-square md:h-auto md:w-full"
                compact
              />
            </div>

            <div className={cx(columns.material, "space-y-2")}>
              <label className={fieldLabelClass}>
                મટીરીયલ <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name={`items.${index}.material_id`}
                rules={{ validate: (value) => Number(value) > 0 }}
                render={({ field: materialField }) => (
                  <button
                    type="button"
                    ref={materialField.ref}
                    disabled={!canPick}
                    title={pickerLabel}
                    onClick={onOpenPicker}
                    className={cx(
                      fieldInputClass(!!materialError),
                      "relative flex w-full items-center bg-white text-left disabled:cursor-not-allowed disabled:opacity-60"
                    )}
                  >
                    <LuPackage className={formFieldIconClass} />
                    <span
                      className={cx(
                        "truncate",
                        !materialField.value && "text-muted-foreground"
                      )}
                    >
                      {materialField.value
                        ? material?.name || `#${materialField.value}`
                        : pickerLabel}
                    </span>
                  </button>
                )}
              />
              {materialError && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  મટીરીયલ પસંદ કરો
                </p>
              )}
            </div>

            <div className={cx(columns.qty, "space-y-2")}>
              <label
                htmlFor={`material-line-qty-${index}`}
                className={fieldLabelClass}
              >
                જથ્થો <span className="text-red-500">*</span>
              </label>
              <Controller
                control={control}
                name={`items.${index}.qty`}
                rules={{ validate: (value) => isWholeQty(value) }}
                render={({ field: qtyField }) => (
                  <div className={cx("relative", qtyError && "error")}>
                    <LuBoxes className={formFieldIconClass} />
                    <Input
                      ref={qtyField.ref}
                      id={`material-line-qty-${index}`}
                      name={qtyField.name}
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="જથ્થો"
                      value={qtyField.value}
                      onChange={(event) => {
                        const qty = sanitizeQty(event.target.value);

                        onQtyChange(index, qtyField.value, qty);
                        qtyField.onChange(qty);
                      }}
                      onBlur={qtyField.onBlur}
                      className={cx(fieldInputClass(!!qtyError), "bg-white")}
                    />
                    {qtyError && (
                      <LuAlertCircle className={fieldErrorIconClass} />
                    )}
                  </div>
                )}
              />
              {qtyError && (
                <p className={fieldErrorTextClass}>
                  <LuAlertCircle className="h-4 w-4 mr-1" />
                  આખો જથ્થો દાખલ કરો
                </p>
              )}
              {pendingText && <p className={hintTextClass}>{pendingText}</p>}
              {locked && (
                <p className={hintTextClass}>
                  મળેલ: {formatQty(hint?.received)}
                  {hint?.closed && " • બંધ"}
                </p>
              )}
              {isOrderLine && (
                <Controller
                  control={control}
                  name={`items.${index}.close_order`}
                  render={({ field: closeField }) => (
                    <label className="flex w-fit cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-600">
                      <input
                        ref={closeField.ref}
                        type="checkbox"
                        className={checkboxClass}
                        checked={!!closeField.value}
                        onChange={(event) =>
                          closeField.onChange(event.target.checked)
                        }
                        onBlur={closeField.onBlur}
                      />
                      ઓર્ડર બંધ
                    </label>
                  )}
                />
              )}
            </div>

            {withPrice && (
              <>
                <div className="md:col-span-2 space-y-2">
                  <label
                    htmlFor={`material-line-price-${index}`}
                    className={fieldLabelClass}
                  >
                    ભાવ <span className="text-red-500">*</span>
                  </label>
                  <Controller
                    control={control}
                    name={`items.${index}.price`}
                    rules={{ validate: (value) => hasRate(value) }}
                    render={({ field: priceField }) => (
                      <RateInput
                        ref={priceField.ref}
                        id={`material-line-price-${index}`}
                        name={priceField.name}
                        value={priceField.value}
                        placeholder="0.00"
                        hasError={!!priceError}
                        onChange={priceField.onChange}
                        onBlur={priceField.onBlur}
                      />
                    )}
                  />
                  {priceError && (
                    <p className={fieldErrorTextClass}>
                      <LuAlertCircle className="h-4 w-4 mr-1" />
                      ભાવ દાખલ કરો (0 પણ ચાલે)
                    </p>
                  )}
                  {(hint?.lastPrice ?? null) !== null && (
                    <p className={hintTextClass}>
                      પહેલાનો રેટ: ₹{formatAmount(hint?.lastPrice)}
                    </p>
                  )}
                </div>

                <div className="md:col-span-2 space-y-2">
                  <label className={fieldLabelClass}>રકમ</label>
                  <div className="relative">
                    <LuIndianRupee className={formFieldIconClass} />
                    <Input
                      type="text"
                      readOnly
                      tabIndex={-1}
                      aria-label={`મટીરીયલ - ${index + 1} રકમ`}
                      value={formatAmount(lineAmount(item))}
                      className={cx(
                        fieldInputClass(),
                        "cursor-not-allowed bg-gray-100 text-right"
                      )}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="hidden md:col-span-1 md:flex md:items-start md:justify-center md:pt-7">
              {removeButton}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        className={arrayAddButtonClass}
        disabled={!canPick}
        onClick={onOpenPicker}
      >
        <LuPlus className="h-4 w-4" />
        મટીરીયલ ઉમેરો
      </button>
    </div>
  );
};

export default MaterialLinesForm;
